import {Button, Cascader, Form, Input, message, Modal, Select, Space, Table, Tag, Typography} from 'antd';
import {useContext, useEffect, useRef, useState} from 'react';
import {Context} from '../Context';
import Helper from '../../func';

const RoleUserPicker = ({roleId, onClose, onAdded}) => {
    const setting = useContext(Context);
    const orgTree = Array.isArray(setting.org_tree) ? setting.org_tree : [];
    const [form] = Form.useForm();
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [reload, setReload] = useState(0);
    const submitting = useRef(false);
    const pageSize = 100;

    useEffect(() => {
        let active = true;
        setLoading(true);
        Helper.http.get(Helper.serviceUrl('role/available_users'), {
            role_id: roleId, page, page_size: pageSize, ...filters,
        }, (res) => {
            if (!active) return;
            setLoading(false);
            if (!res.success) {
                setUsers([]);
                setTotal(0);
                message.error(res.msg || '可添加用户加载失败');
                return;
            }
            setUsers((res.result || []).map((user) => ({...user, u_id: Number(user.u_id)})));
            setTotal(Number(res.total) || 0);
        }, () => {
            if (!active) return;
            setLoading(false);
            setUsers([]);
            setTotal(0);
            message.error('可添加用户加载失败');
        });
        return () => { active = false; };
    }, [roleId, page, filters, reload]);

    const search = (values) => {
        setSelectedIds([]);
        setPage(1);
        setFilters({
            u_account: (values.u_account || '').trim(),
            u_nickname: (values.u_nickname || '').trim(),
            em_org_info: (values.em_org_info || []).map((path) => path.join('/')).join(','),
            u_status: values.u_status || '',
            em_status: values.em_status || '',
        });
    };

    const addSelected = () => {
        if (submitting.current || loading || selectedIds.length === 0) return;
        submitting.current = true;
        setSaving(true);
        Helper.http.post(Helper.serviceUrl('role/add_users'), {
            role_id: roleId, u_ids: selectedIds,
        }, (res) => {
            submitting.current = false;
            setSaving(false);
            if (!res.success) {
                message.error(res.msg || '添加失败');
                return;
            }
            message.success(res.msg || '添加成功');
            setSelectedIds([]);
            setPage(1);
            setReload((value) => value + 1);
            onAdded();
        }, () => {
            submitting.current = false;
            setSaving(false);
            message.error('添加失败');
        });
    };

    const formatOrg = (value) => (value || '').split(',').filter(Boolean).map((path) => {
        let options = orgTree;
        return path.split('/').filter(Boolean).map((part) => {
            const node = options.find((item) => item.value === part);
            options = node && Array.isArray(node.children) ? node.children : [];
            return node ? node.label : part;
        }).join(' / ');
    }).join('，') || '未绑定组织';

    return <Modal
        visible
        title="筛选并添加用户"
        width={900}
        onCancel={onClose}
        closable={!saving}
        maskClosable={!saving}
        keyboard={!saving}
        footer={<Space>
            <Button onClick={onClose} disabled={saving}>关闭</Button>
            <Button type="primary" loading={saving} disabled={loading || selectedIds.length === 0} onClick={addSelected}>
                添加所选用户（{selectedIds.length}）
            </Button>
        </Space>}
    >
        <Form form={form} layout="vertical" onFinish={search} initialValues={{u_status: '', em_status: ''}}>
            <Space wrap align="end">
                <Form.Item label="员工组织架构" name="em_org_info">
                    <Cascader options={orgTree} multiple allowClear showSearch
                        showCheckedStrategy={Cascader.SHOW_PARENT}
                        placeholder="多选组织，满足任一项（含下级）" style={{width: 300}} disabled={saving}/>
                </Form.Item>
                <Form.Item label="账号" name="u_account">
                    <Input allowClear placeholder="账号关键词" disabled={saving}/>
                </Form.Item>
                <Form.Item label="昵称" name="u_nickname">
                    <Input allowClear placeholder="昵称关键词" disabled={saving}/>
                </Form.Item>
                <Form.Item label="账号状态" name="u_status">
                    <Select style={{width: 140}} disabled={saving} options={[
                        {value: '', label: '不限'},
                        {value: '9', label: '已启用'},
                        {value: '1', label: '已禁用'},
                    ]}/>
                </Form.Item>
                <Form.Item label="员工在职状态" name="em_status">
                    <Select style={{width: 140}} disabled={saving} options={[
                        {value: '', label: '不限'},
                        {value: '9', label: '在职中'},
                        {value: '2', label: '停职中'},
                        {value: '1', label: '已离职'},
                    ]}/>
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button htmlType="submit" type="primary" disabled={saving}>筛选</Button>
                        <Button disabled={saving} onClick={() => {form.resetFields(); search({});}}>重置</Button>
                    </Space>
                </Form.Item>
            </Space>
        </Form>
        <Space wrap style={{marginBottom: 12}}>
            <Typography.Text type="secondary">找到 {total} 名未加入该组的用户；已选 {selectedIds.length} 名，支持跨页勾选</Typography.Text>
            <Button type="link" disabled={saving || selectedIds.length === 0} onClick={() => setSelectedIds([])}>清空勾选</Button>
        </Space>
        <Table
            size="small"
            rowKey="u_id"
            loading={loading || saving}
            dataSource={users}
            scroll={{x: 850}}
            rowSelection={{
                selectedRowKeys: selectedIds,
                preserveSelectedRowKeys: true,
                onChange: (keys) => setSelectedIds(keys),
                getCheckboxProps: (user) => ({disabled: loading || saving, 'aria-label': `选择用户 ${user.u_account}`}),
            }}
            columns={[
                {title: '账号', dataIndex: 'u_account'},
                {title: '昵称', dataIndex: 'u_nickname'},
                {title: '员工', render: (_, user) => [user.em_name, user.em_num].filter(Boolean).join(' / ') || '未绑定员工'},
                {title: '组织架构', dataIndex: 'em_org_info', render: formatOrg},
                {title: '账号状态', dataIndex: 'u_status', render: (value) => Number(value) === 9
                    ? <Tag color="green">已启用</Tag> : <Tag>已禁用</Tag>},
                {title: '员工在职状态', dataIndex: 'em_status', render: (value) =>
                    ({9: '在职中', 2: '停职中', 1: '已离职'})[value] || '未绑定员工'},
            ]}
            pagination={{current: page, pageSize, total, showSizeChanger: false,
                onChange: setPage, disabled: saving}}
        />
    </Modal>;
};

export default RoleUserPicker;
